import { PageShell } from './PageShell.js';
import { translate, type WashedLocale } from '@washed/i18n';
import styles from './ZonesPage.module.css';

interface PageProps {
  locale: WashedLocale;
  onLocaleChange: (locale: WashedLocale) => void;
}

type AreaGroup = {
  name: string;
  noteFr: string;
  noteEn: string;
  places: string[];
};

const AREA_GROUPS: AreaGroup[] = [
  {
    name: 'Golfe 1',
    noteFr: 'Bè et le sud-est de Lomé',
    noteEn: 'Bè and south-east Lomé',
    places: [
      'Bè-Kpota',
      'Bè-Apéyémé',
      'Bè-Adzrométi',
      'Bè-Ahligo',
      'Ablogamé',
      'Akodésséwa',
      'Adakpamé',
      'Anfamé',
      'Katanga',
      'Kélégougan',
    ],
  },
  {
    name: 'Golfe 2',
    noteFr: 'Hédzranawoé, Tokoin et Kégué',
    noteEn: 'Hédzranawoé, Tokoin and Kégué',
    places: [
      'Hédzranawoé',
      'Tokoin Wuiti',
      'Tokoin Tamé',
      'Tokoin Enyonam',
      'Tokoin Aviation',
      'Kégué',
    ],
  },
  {
    name: 'Golfe 3',
    noteFr: 'Le cœur de Tokoin',
    noteEn: 'Central Tokoin',
    places: [
      'Tokoin-Elavagnon',
      'Tokoin-Gbonvié',
      'Doumasséssé / Adéwui',
      'Tokoin-Klévé',
      'Tokoin-Atchanti',
    ],
  },
  {
    name: 'Golfe 5',
    noteFr: 'Ouest de Lomé',
    noteEn: 'West Lomé',
    places: [
      'Adidogomé',
      'Djidjolé',
      'Agbalépédogan',
      'Adidoadin',
      'Avédji',
      'Amadahomé',
      'Apédokoè',
    ],
  },
  {
    name: 'Golfe 6',
    noteFr: 'Baguida et le littoral est',
    noteEn: 'Baguida and the eastern coast',
    places: ['Baguida', 'Avépozo', 'Adamavo', 'Kpogan'],
  },
  {
    name: 'Agoè-Nyivé',
    noteFr: 'Grand nord de Lomé',
    noteEn: 'Greater north Lomé',
    places: ['Agoè', 'Sogbossito', 'Logopé', 'Totsi', 'Cacavéli'],
  },
];

const COPY: Record<
  WashedLocale,
  { eyebrow: string; title: string; intro: string; noteTitle: string; note: string }
> = {
  // Coverage copy comes from the deck: every Lomé quartier is served (#566).
  fr: {
    eyebrow: 'Washed · Zones',
    title: translate('marketing.zones.title', 'fr'),
    intro: translate('marketing.zones.intro', 'fr'),
    noteTitle: translate('marketing.zones.note.title', 'fr'),
    note: translate('marketing.zones.note.body', 'fr'),
  },
  en: {
    eyebrow: 'Washed · Service areas',
    title: translate('marketing.zones.title', 'en'),
    intro: translate('marketing.zones.intro', 'en'),
    noteTitle: translate('marketing.zones.note.title', 'en'),
    note: translate('marketing.zones.note.body', 'en'),
  },
};

export function ZonesPage({ locale, onLocaleChange }: PageProps) {
  const c = COPY[locale];
  const itemList = AREA_GROUPS.flatMap((group) => group.places).map((name, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name,
  }));
  const structured = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://washedafrica.com/zones#webpage',
        url: 'https://washedafrica.com/zones',
        name: c.title,
        description: c.intro,
        inLanguage: locale,
        isPartOf: { '@id': 'https://washedafrica.com/#website' },
        about: { '@id': 'https://washedafrica.com/#organization' },
      },
      {
        '@type': 'ItemList',
        '@id': 'https://washedafrica.com/zones#areas',
        name: translate('marketing.zones.list.name', locale),
        itemListElement: itemList,
      },
    ],
  };

  return (
    <PageShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      eyebrow={c.eyebrow}
      title={c.title}
      intro={c.intro}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structured) }}
      />

      <p className={styles.intro}>{translate('marketing.zones.list.intro', locale)}</p>

      <div className={styles.grid}>
        {AREA_GROUPS.map((group) => (
          <section key={group.name} className={styles.card}>
            <h2>{group.name}</h2>
            <p>{locale === 'fr' ? group.noteFr : group.noteEn}</p>
            <ul className={styles.neighbourhoods}>
              {group.places.map((place) => (
                <li key={place}>{place}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <section className={styles.note}>
        <h2>{c.noteTitle}</h2>
        <p>{c.note}</p>
      </section>
    </PageShell>
  );
}
